import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import deviceFetch from "../utils/deviceFetch";
import normalize, { fuzzyIncludes } from "../utils/normalize";
import { useParams, useNavigate, Link } from "react-router-dom";

const QUEUE_KEY_PREFIX = "turnstileQueue:";

function Turnstile() {
  const { deviceId } = useParams();

  const nav = useNavigate();

  const deviceTokenKey = `turnstileDeviceToken:${deviceId || "default"}`;

  const [deviceToken, setDeviceToken] = useState(() => {
    try {
      return localStorage.getItem(deviceTokenKey) || "";
    } catch {
      return "";
    }
  });

  useEffect(() => {
    try {
      const t = localStorage.getItem(deviceTokenKey) || "";
      setDeviceToken(t);
    } catch {
      setDeviceToken("");
    }
  }, [deviceTokenKey]);

  useEffect(() => {
    if (!deviceToken) {
      nav("/turnstile-login", { replace: true });
    }
  }, [deviceToken, nav]);

  const [device, setDevice] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [search, setSearch] = useState("");

  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [isCameraReady, setIsCameraReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queue, setQueue] = useState([]);

  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);

  const [statusMessage, setStatusMessage] = useState(null);
  const [statusType, setStatusType] = useState("info"); // info | success | error

  const [lastEvent, setLastEvent] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [capturedPhotoUrl, setCapturedPhotoUrl] = useState(null); // dataURL
  const capturedPhotoUrlRef = useRef(null);

  const [isFlushingQueue, setIsFlushingQueue] = useState(false);

  const [cameraOn, setCameraOn] = useState(false);
  const [autoStatus, setAutoStatus] = useState("idle"); 
  // 'idle' | 'recognizing' | 'success' | 'already_marked' | 'unknown' | 'service_unavailable' | 'error'
  const [autoEmployee, setAutoEmployee] = useState(null);
  const [showManualPanel, setShowManualPanel] = useState(false);

  const isAutoMode = cameraOn && !showManualPanel;
  const autoTimerRef = useRef(null);

  const queueKey = `${QUEUE_KEY_PREFIX}${deviceId || "default"}`;

  // Утилита для статусов
  const showStatus = useCallback((message, type = "info", timeout = 3000) => {
    setStatusMessage(message);
    setStatusType(type);

    if (timeout) {
      setTimeout(() => {
        setStatusMessage(null);
      }, timeout);
    }
  }, []);

  // Загрузка устройства и сотрудников
  useEffect(() => {
    if (!deviceId) return;

    const fetchData = async () => {
      try {
        if (!deviceToken) return; // без токена не грузим

        const res = await deviceFetch(
          `/api/turnstile/devices/${deviceId}/employees?onlyNotMarkedToday=true`,
          { cache: "no-store", deviceToken }
        );

        if (!res.ok) {
          throw new Error("Failed to load device or employees");
        }

        const data = await res.json();
        setDevice(data.device);
        setEmployees(data.users || []);
        setFilteredEmployees(data.users || []);
      } catch (err) {
        console.error(err);
        showStatus("Не удалось загрузить список сотрудников", "error", 5000);
      }
    };

    fetchData();
  }, [deviceId, deviceToken, showStatus]);

  // Фильтрация сотрудников по поиску (с транслитерацией)
  useEffect(() => {
    if (!search?.trim()) {
      setFilteredEmployees(employees);
      return;
    }

    setFilteredEmployees(
      employees.filter((emp) => {
        const fullName = normalize(
          emp.full_name ||
            `${emp.last_name || ""} ${emp.first_name || ""}` ||
            ""
        );
        return fuzzyIncludes(fullName, search);
      })
    );
  }, [search, employees]);

  // Геолокация устройства (один раз)
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationError("Геолокация не поддерживается этим устройством");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
        setLocationError(null);
      },
      (err) => {
        console.error("Geolocation error:", err);
        setLocationError("Не удалось получить геопозицию (разрешите доступ)");
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
      }
    );
  }, []);

  // Online / offline
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Загрузка очереди из localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(queueKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setQueue(parsed);
        }
      }
    } catch (err) {
      console.error("Failed to load turnstile queue:", err);
    }
  }, [queueKey]);

  // Синхронизация очереди в localStorage
  useEffect(() => {
    try {
      localStorage.setItem(queueKey, JSON.stringify(queue));
    } catch (err) {
      console.error("Failed to save turnstile queue:", err);
    }
  }, [queue, queueKey]);

  // Старт камеры
  const startCamera = useCallback(async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      showStatus("Камера не поддерживается этим устройством", "error", 5000);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      streamRef.current = stream;
      setIsCameraReady(true);
      setCameraOn(true); 
    } catch (err) {
      console.error("Camera error:", err);
      showStatus("Не удалось открыть камеру (проверьте разрешения)", "error", 5000);
      setCameraOn(false);
      setIsCameraReady(false);
    }
  }, [showStatus]);

  // Остановка камеры (если понадобится)
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraOn(false);
    setCapturedPhotoUrl(null);
    setIsCameraReady(false);
    capturedPhotoUrlRef.current = null;
    setAutoEmployee(null);
    setAutoStatus("idle");
  }, []);

  // Клик по сотруднику
  const handleEmployeeClick = (emp) => {
    setSelectedEmployee(emp);
    setCapturedPhotoUrl(null);
    capturedPhotoUrlRef.current = null;
  };

  // Сфотографировать
  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current) {
      showStatus("Камера ещё не готова", "error");
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, width, height);

    const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
    setCapturedPhotoUrl(dataUrl);
    capturedPhotoUrlRef.current = dataUrl;
  };

    //включить / выключить камеру
  const handleToggleCamera = async () => {
    if (cameraOn) {
      stopCamera();
    } else {
      await startCamera();
    }
  };

  //авто-распознавание по лицу
  const handleRecognize = useCallback(async () => {
    if (!deviceId) {
      showStatus("Не указан идентификатор устройства", "error", 4000);
      return;
    }

    if (!deviceToken) {
      nav("/turnstile-login", { replace: true });
      return;
    }

    if (!isOnline) {
      showStatus(
        "Автоматическое распознавание недоступно в оффлайне, используйте ручной поиск",
        "error",
        5000
      );
      setShowManualPanel(true);
      return;
    }

    if (!isCameraReady || !videoRef.current || !canvasRef.current) {
      showStatus("Камера ещё не готова", "error", 4000);
      return;
    }

    // Берём кадр с камеры
    const video = videoRef.current;
    const canvas = canvasRef.current;

    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!video.videoWidth || !video.videoHeight) {
      // видео ещё не прогрузило метаданные
      return;
    }
    ctx.drawImage(video, 0, 0, width, height);

    const dataUrl = canvas.toDataURL("image/jpeg", 0.8);

    setAutoStatus("recognizing");
    setAutoEmployee(null);
    setStatusMessage(null);

    try {
      const res = await deviceFetch("/api/turnstile/recognize", {
        method: "POST",
        deviceToken,
        body: {
          image: dataUrl,
          geo_lat: location?.lat ?? null,
          geo_lng: location?.lng ?? null,
          geo_accuracy_m: location?.accuracy ?? null,
        },
      });

      const data = await res.json();

      if (!res.ok) {
        // Сервис упал / недоступен
        setAutoStatus("service_unavailable");
        showStatus(
          "Сервис распознавания недоступен, используйте ручной поиск",
          "error",
          5000
        );
        setShowManualPanel(true);
        return;
      }

      // data.status: ok | already_marked | unknown | service_unavailable
      if (data.status === "ok") {
        setAutoStatus("success");
        setAutoEmployee(data.employee || null);
        // setCapturedPhotoUrl(dataUrl);
        // capturedPhotoUrlRef.current = dataUrl;

        if (data.employee?.id) {
          // убираем сотрудника из списка, чтобы не дублировался
          setEmployees((prev) => prev.filter((u) => u.id !== data.employee.id));
        }

        setLastEvent({
          employeeName: data.employee?.full_name || "Неизвестно",
          eventTime: data.event?.event_time || new Date().toISOString(),
          deviceName: device?.name,
          insideGeo: data.insideGeo,
        });

        showStatus(
          `Отметился: ${data.employee?.full_name || "сотрудник"}`,
          "success",
          2500
        );
      } else if (data.status === "already_marked") {
        setAutoStatus("already_marked");
        setAutoEmployee(data.employee || null);
        // setCapturedPhotoUrl(dataUrl);
        // capturedPhotoUrlRef.current = dataUrl;

        showStatus(
          `Сотрудник уже отметился сегодня: ${
            data.employee?.full_name || "сотрудник"
          }`,
          "info",
          4000
        );
      } else if (data.status === "unknown") {
        setAutoStatus("unknown");
        setAutoEmployee(null);
        showStatus(
          "Не удалось распознать лицо, используйте ручной поиск",
          "error",
          5000
        );
        setShowManualPanel(true);
      } else if (data.status === "service_unavailable") {
        setAutoStatus("service_unavailable");
        setAutoEmployee(null);
        showStatus(
          "Сервис распознавания недоступен, используйте ручной поиск",
          "error",
          5000
        );
        setShowManualPanel(true);
      } else {
        setAutoStatus("error");
        setAutoEmployee(null);
        showStatus("Ошибка при распознавании лица", "error", 5000);
      }
    } catch (err) {
      console.error("handleRecognize error:", err);
      setAutoStatus("error");
      showStatus("Ошибка при распознавании лица", "error", 5000);
      setShowManualPanel(true);
    }
  }, [deviceId, deviceToken, isOnline, isCameraReady, location, device, showStatus, nav]);

  // Авто-сканирование лица, пока камера включена
  useEffect(() => {
    if (!isAutoMode || !isCameraReady || !isOnline) return;

    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;

      // не спамим, если уже идет распознавание
      if (autoStatus === "recognizing") {
        autoTimerRef.current = setTimeout(tick, 500);
        return;
      }

      await handleRecognize();

      // пауза после завершения запроса (регулируй)
      autoTimerRef.current = setTimeout(tick, 1800);
    };

    // стартуем не мгновенно, чтобы видео успело "разогнаться"
    autoTimerRef.current = setTimeout(tick, 900);

    return () => {
      cancelled = true;
      if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    };
  }, [isAutoMode, isCameraReady, isOnline, autoStatus, handleRecognize]);

  // Положить событие в очередь (оффлайн)
  const enqueueEvent = useCallback(
    (payload) => {
      const newEvent = {
        ...payload,
        tempId: crypto.randomUUID(),
      };
      setQueue((prev) => [...prev, newEvent]);
      showStatus("Нет связи: отметка сохранена локально", "info", 4000);
    },
    [showStatus]
  );

  // Отправка очереди при появлении сети
  const flushQueue = useCallback(async () => {
    if (!isOnline || isFlushingQueue || !queue.length) return;
    if (!deviceId) return;
    if (!deviceToken) {
      nav("/turnstile-login", { replace: true });
      return;
    }

    setIsFlushingQueue(true);
    try {
      const newQueue = [];

      for (const item of queue) {
        try {
          // 1) грузим фотку в Directus
          const uploadRes = await deviceFetch("/api/turnstile/upload-photo", {
            method: "POST",
            deviceToken,
            body: { image: item.photoDataUrl, fileName: `turnstile-${item.user_id}-${item.event_time}.jpg` },
          });
          if (!uploadRes.ok) throw new Error("upload-photo failed");
          const uploadData = await uploadRes.json();
          const fileId = uploadData.file_id;

          // 2) создаём событие
          const eventRes = await deviceFetch("/api/turnstile/events", {
            method: "POST",
            deviceToken,
            body: {
              user_id: item.user_id,
              event_time: item.event_time,
              geo_lat: item.geo_lat,
              geo_lng: item.geo_lng,
              geo_accuracy_m: item.geo_accuracy_m,
              photo_file_id: fileId,
            },
          });
          if (!eventRes.ok) throw new Error("createEvent failed");
          const eventData = await eventRes.json();

          setLastEvent({
            employeeName: item.employeeName,
            eventTime: item.event_time,
            deviceName: device?.name,
            insideGeo: eventData.insideGeo,
          });
        } catch (err) {
          console.error("flushQueue item failed:", err);
          // Не удаляем это событие, попытаемся позже
          newQueue.push(item);
        }
      }

      setQueue(newQueue);
      if (!newQueue.length) {
        showStatus("Все локальные отметки отправлены", "success", 3000);
      }
    } finally {
      setIsFlushingQueue(false);
    }
  }, [queue, isOnline, isFlushingQueue, deviceId, deviceToken, device, showStatus, nav]);

  // Авто-флаш при появлении сети
  useEffect(() => {
    if (isOnline && queue.length) {
      flushQueue();
    }
  }, [isOnline, queue.length, flushQueue]);

  // Отправка текущей отметки
  const handleSubmit = async () => {
    if (!deviceId) {
      showStatus("Не указан идентификатор устройства", "error");
      return;
    }

    if (!deviceToken) {
      nav("/turnstile-login", { replace: true });
      return;
    }

    if (!selectedEmployee) {
      showStatus("Выберите сотрудника", "error");
      return;
    }

    if (!capturedPhotoUrlRef.current) {
      showStatus("Сначала сделайте фото", "error");
      return;
    }

    const now = new Date();
    const eventTime = now.toISOString();

    const basePayload = {
      device_id: Number(deviceId),
      user_id: selectedEmployee.id,
      event_time: eventTime,
      geo_lat: location?.lat ?? null,
      geo_lng: location?.lng ?? null,
      geo_accuracy_m: location?.accuracy ?? null,
      photoDataUrl: capturedPhotoUrlRef.current,
      employeeName:
        selectedEmployee.full_name ||
        `${selectedEmployee.last_name || ""} ${selectedEmployee.first_name || ""}`.trim(),
    };

    // Если оффлайн — сразу в очередь
    if (!isOnline) {
      enqueueEvent(basePayload);
      // локально "убираем" сотрудника из списка
      setEmployees((prev) => prev.filter((u) => u.id !== selectedEmployee.id));
      setSelectedEmployee(null);
      setCapturedPhotoUrl(null);
      capturedPhotoUrlRef.current = null;
      return;
    }

    setIsSubmitting(true);
    try {
      // 1) upload-photo
      const uploadRes = await deviceFetch("/api/turnstile/upload-photo", {
        method: "POST",
        deviceToken,
        body: {
          image: basePayload.photoDataUrl,
          fileName: `turnstile-${basePayload.user_id}-${basePayload.event_time}.jpg`,
        },
      });

      if (!uploadRes.ok) throw new Error("upload-photo failed");
      const uploadData = await uploadRes.json();
      const fileId = uploadData.file_id;

      // 2) createEvent
      const eventRes = await deviceFetch("/api/turnstile/events", {
        method: "POST",
        deviceToken,
        body: {
          user_id: basePayload.user_id,
          event_time: basePayload.event_time,
          geo_lat: basePayload.geo_lat,
          geo_lng: basePayload.geo_lng,
          geo_accuracy_m: basePayload.geo_accuracy_m,
          photo_file_id: fileId,
        },
      });

      if (!eventRes.ok) {
        throw new Error("createEvent failed");
      }

      const eventData = await eventRes.json();

      setLastEvent({
        employeeName: basePayload.employeeName,
        eventTime: basePayload.event_time,
        deviceName: device?.name,
        insideGeo: eventData.insideGeo,
      });

      // Убираем сотрудника из списка (чтобы не дублировал сегодня)
      setEmployees((prev) => prev.filter((u) => u.id !== selectedEmployee.id));
      setSelectedEmployee(null);
      setCapturedPhotoUrl(null);
      capturedPhotoUrlRef.current = null;

      showStatus("Отметка сохранена", "success", 2000);
    } catch (err) {
      console.error("submit error:", err);
      // Если сервер/сеть упали — уходим в оффлайн-очередь
      enqueueEvent(basePayload);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Чистим камеру при размонтировании
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  const queueCount = queue.length;

  return (
    // <Layout>
      <div className="container">
        <div className="turnstile-page">
          <header className="turnstile-header">
            <div className="turnstile-header__title-block">
              <h1 className="turnstile-header__title">
                Турникет{device ? ` – ${device.name}` : ""}
              </h1>
              {device && (
                <div className="turnstile-header__meta">
                  <span className="turnstile-header__branch">
                    Филиал: {device.branch || "—"}
                  </span>
                  <span className="turnstile-header__device-id">
                    Устройство #{device.id}
                  </span>
                </div>
              )}
            </div>

            <div className="turnstile-header__status">
              <div>
                <span
                  className={`turnstile-chip ${
                    isOnline ? "turnstile-chip--success" : "turnstile-chip--error"
                  }`}
                >
                  {isOnline ? "Онлайн" : "Оффлайн"}
                </span>

                {location && (
                  <span className="turnstile-chip turnstile-chip--info">
                    {/* {location.lat.toFixed(5)}, {location.lng.toFixed(5)} */}
                    GPS:  ±{Math.round(location.accuracy)} м
                  </span>
                )}

                {locationError && (
                  <span className="turnstile-chip turnstile-chip--warning">
                    {locationError}
                  </span>
                )}

                {queueCount > 0 && (
                  <span className="turnstile-chip turnstile-chip--warning">
                    В очереди: {queueCount}
                  </span>
                )}
              </div>
              <Link to={`/turnstile/${deviceId}/enroll`} className="turnstile-button turnstile-button--secondary">
                Регистрация лица
              </Link>
            </div>
          </header>

          {statusMessage && (
            <div className={`turnstile-status turnstile-status--${statusType}`}>
              {statusMessage}
            </div>
          )}

          <main className="turnstile-body">
            {/* Левая колонка – поиск и список сотрудников (по умолчанию скрыта) */}
            {showManualPanel && (
              <section className="turnstile-column turnstile-column--left">
                <div className="turnstile-search">
                  <input
                    type="text"
                    className="turnstile-search__input"
                    placeholder="Поиск по имени сотрудника..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <div className="turnstile-employees-list">
                  {filteredEmployees.map((emp) => {
                    const fullName =
                      emp.full_name ||
                      `${emp.last_name || ""} ${emp.first_name || ""}`.trim();

                    const isSelected = selectedEmployee?.id === emp.id;

                    return (
                      <button
                        key={emp.id}
                        className={`turnstile-employee ${
                          isSelected ? "turnstile-employee--selected" : ""
                        }`}
                        onClick={() => handleEmployeeClick(emp)}
                      >
                        <span className="turnstile-employee__name">{fullName}</span>
                        {emp.position && (
                          <span className="turnstile-employee__position">
                            {emp.position}
                          </span>
                        )}
                      </button>
                    );
                  })}

                  {!filteredEmployees.length && (
                    <div className="turnstile-placeholder">
                      Нет сотрудников по фильтру
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Правая колонка – камера, фото, последняя отметка */}
            <section className="turnstile-column turnstile-column--right">
              <div className="turnstile-camera">
                <div className="turnstile-camera__header">
                  <h2 className="turnstile-camera__title">
                    {selectedEmployee
                      ? `Сотрудник: ${
                          selectedEmployee.full_name ||
                          `${selectedEmployee.last_name || ""} ${
                            selectedEmployee.first_name || ""
                          }`.trim()
                        }`
                      : "Автоматическое распознавание лица"}
                  </h2>

                  <div className="turnstile-camera__header-actions">
                    <button
                      type="button"
                      className="turnstile-button turnstile-button--secondary"
                      onClick={handleToggleCamera}
                    >
                      {cameraOn ? "Выключить камеру" : "Включить камеру"}
                    </button>

                    <button
                      type="button"
                      className="turnstile-button turnstile-button--secondary"
                      onClick={() => setShowManualPanel((prev) => !prev)}
                    >
                      {showManualPanel ? "Скрыть ручной поиск" : "Ручной поиск"}
                    </button>
                  </div>
                </div>

                <div className="turnstile-camera__content">
                  <div className="turnstile-camera__preview">
                    <div className="turnstile-camera__video-wrapper">
                      <video
                        ref={videoRef}
                        className="turnstile-camera__video"
                        autoPlay
                        playsInline
                        muted
                      />

                      {!isCameraReady && (
                        <div className="turnstile-camera__overlay">
                          Камера готовится...
                        </div>
                      )}

                      {showManualPanel && capturedPhotoUrl && (
                        <img
                          src={capturedPhotoUrl}
                          alt="Снимок"
                          className="turnstile-camera__image turnstile-camera__image--overlay"
                        />
                      )}
                    </div>

                    <canvas
                      ref={canvasRef}
                      className="turnstile-camera__canvas"
                      style={{ display: "none" }}
                    />
                  </div>

                  <div className="turnstile-actions">
                    {showManualPanel && (
                      <>
                        <button
                          type="button"
                          className="turnstile-button turnstile-button--secondary"
                          onClick={handleCapture}
                          disabled={
                            !selectedEmployee || !isCameraReady
                          }
                        >
                          {capturedPhotoUrl ? "Переснять" : "Сделать фото (ручной)"}
                        </button>

                        <button
                          type="button"
                          className="turnstile-button turnstile-button--secondary"
                          onClick={handleSubmit}
                          disabled={
                            !selectedEmployee ||
                            !capturedPhotoUrl ||
                            isSubmitting
                          }
                        >
                          {isSubmitting ? "Сохраняем..." : "Готово / Отметить вручную"}
                        </button>
                      </>
                    )}
                  </div>

                  {autoStatus !== "idle" && (
                    <div
                      className={`turnstile-auto-status turnstile-auto-status--${autoStatus}`}
                    >
                      {autoStatus === "success" && autoEmployee && (
                        <>
                          <div className="turnstile-auto-status__title">
                            Распознано лицо
                          </div>
                          <div className="turnstile-auto-status__text">
                            {autoEmployee.full_name} – доступ разрешён
                          </div>
                        </>
                      )}

                      {autoStatus === "already_marked" && autoEmployee && (
                        <>
                          <div className="turnstile-auto-status__title">
                            Уже отметился
                          </div>
                          <div className="turnstile-auto-status__text">
                            {autoEmployee.full_name} уже отметился сегодня
                          </div>
                        </>
                      )}

                      {autoStatus === "unknown" && (
                        <>
                          <div className="turnstile-auto-status__title">
                            Не удалось распознать лицо
                          </div>
                          <div className="turnstile-auto-status__text">
                            Используйте ручной поиск для отметки сотрудника.
                          </div>
                        </>
                      )}

                      {autoStatus === "service_unavailable" && (
                        <>
                          <div className="turnstile-auto-status__title">
                            Сервис распознавания недоступен
                          </div>
                          <div className="turnstile-auto-status__text">
                            Используйте ручной поиск до восстановления сервиса.
                          </div>
                        </>
                      )}

                      {autoStatus === "error" && (
                        <>
                          <div className="turnstile-auto-status__title">
                            Ошибка распознавания
                          </div>
                          <div className="turnstile-auto-status__text">
                            Попробуйте ещё раз или отметьтесь вручную.
                          </div>
                        </>
                      )}

                      {autoStatus === "recognizing" && (
                        <>
                          <div className="turnstile-auto-status__title">
                            Распознаём лицо...
                          </div>
                          <div className="turnstile-auto-status__text">
                            Пожалуйста, не убирайте лицо из кадра.
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {lastEvent && (
                  <div className="turnstile-last-event">
                    <div className="turnstile-last-event__title">
                      Последняя отметка
                    </div>
                    <div className="turnstile-last-event__body">
                      <div className="turnstile-last-event__row">
                        <span className="turnstile-last-event__label">
                          Сотрудник:
                        </span>
                        <span className="turnstile-last-event__value">
                          {lastEvent.employeeName}
                        </span>
                      </div>
                      <div className="turnstile-last-event__row">
                        <span className="turnstile-last-event__label">
                          Время:
                        </span>
                        <span className="turnstile-last-event__value">
                          {new Date(lastEvent.eventTime).toLocaleTimeString("ru-RU", {
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                          })}
                        </span>
                      </div>
                      {lastEvent.deviceName && (
                        <div className="turnstile-last-event__row">
                          <span className="turnstile-last-event__label">
                            Устройство:
                          </span>
                          <span className="turnstile-last-event__value">
                            {lastEvent.deviceName}
                          </span>
                        </div>
                      )}
                      {lastEvent.insideGeo != null && (
                        <div className="turnstile-last-event__row">
                          <span className="turnstile-last-event__label">
                            Геозона:
                          </span>
                          <span
                            className={`turnstile-last-event__value ${
                              lastEvent.insideGeo
                                ? "turnstile-last-event__value--ok"
                                : "turnstile-last-event__value--fail"
                            }`}
                          >
                            {lastEvent.insideGeo ? "Внутри" : "Вне зоны"}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </section>
          </main>
        </div>
      </div>
    // </Layout>
  );
}

export default Turnstile;